import { useEffect, useState, useRef } from 'react';
import { getFunctionUrl } from '../../api/functionUrl.js';
import {
  readPortalTokenFromFragment,
  missingPortalTokenMessage,
} from '../../utils/portalLinkToken.js';

/**
 * Candidate magic-link verification page.
 * Mounted at /candidate-portal-verify?token=xxx
 * Calls employee-verify-magic-link with type=candidate.
 * On success, stores the JWT and hands control back via onVerified().
 */
export default function CandidateMagicLinkVerify({ tenantId, onVerified }) {
  const [status, setStatus] = useState('verifying'); // verifying | error
  const [error, setError] = useState('');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    // GH #6767 — fragment, not query string. See portalLinkToken.js.
    const token = readPortalTokenFromFragment();

    if (!token) {
      setStatus('error');
      setError(missingPortalTokenMessage('invitation link'));
      return;
    }

    (async () => {
      try {
        const res = await fetch(getFunctionUrl('employee-verify-magic-link'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, type: 'candidate', tenantId }),
        });
        const data = await res.json();
        if (!res.ok || !data.token) {
          setStatus('error');
          setError(
            data.error ||
              'This link has expired or already been used. Please request a new one from your recruiter.'
          );
          return;
        }
        onVerified(data.token);
      } catch {
        setStatus('error');
        setError('Could not verify your link. Please check your connection and try again.');
      }
    })();
  }, []);

  const styles = {
    page: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      padding: 24,
    },
    card: {
      background: 'var(--bg2)',
      border: '1px solid var(--border)',
      borderRadius: 16,
      padding: '48px 40px',
      textAlign: 'center',
      maxWidth: 420,
      width: '100%',
    },
    icon: {
      fontSize: 40,
      marginBottom: 20,
    },
    title: {
      fontFamily: 'var(--font-display)',
      fontSize: 20,
      fontWeight: 700,
      color: 'var(--fg)',
      marginBottom: 12,
    },
    body: {
      fontSize: 14,
      color: 'var(--fg3)',
      lineHeight: 1.6,
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {status === 'verifying' ? (
          <>
            <div style={styles.icon}>🔐</div>
            <div style={styles.title}>Verifying your link…</div>
            <div style={styles.body}>Just a moment while we confirm your access.</div>
          </>
        ) : (
          <>
            <div style={styles.icon}>⚠️</div>
            <div style={styles.title}>Link not valid</div>
            <div style={{ ...styles.body, color: 'var(--danger)' }}>{error}</div>
          </>
        )}
      </div>
    </div>
  );
}
